type Point = readonly [number, number, number];

export type HeightmapOptions = {
  reliefHeight: number;
  mode: "emboss" | "engraved" | "both";
  smooth: boolean;
};

type HeightmapResult = {
  blob: Blob;
  width: number;
  height: number;
  depth: number;
  triangleCount: number;
};

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("IMAGE_DECODE_FAILED"));
    };
    image.src = url;
  });
}

function smoothValues(values: Float32Array, width: number, height: number) {
  const next = new Float32Array(values.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let total = 0;
      let count = 0;
      for (let oy = -1; oy <= 1; oy += 1) {
        for (let ox = -1; ox <= 1; ox += 1) {
          const px = Math.min(width - 1, Math.max(0, x + ox));
          const py = Math.min(height - 1, Math.max(0, y + oy));
          total += values[py * width + px];
          count += 1;
        }
      }
      next[y * width + x] = total / count;
    }
  }
  return next;
}

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

function autoLevels(values: Float32Array) {
  const histogram = new Uint32Array(256);
  values.forEach((value) => {
    histogram[Math.min(255, Math.max(0, Math.round(value * 255)))] += 1;
  });

  const lowTarget = values.length * 0.02;
  const highTarget = values.length * 0.98;
  let lowBucket = 0;
  let highBucket = 255;
  let seen = 0;
  for (let index = 0; index < histogram.length; index += 1) {
    seen += histogram[index];
    if (seen >= lowTarget) {
      lowBucket = index;
      break;
    }
  }
  seen = 0;
  for (let index = 0; index < histogram.length; index += 1) {
    seen += histogram[index];
    if (seen >= highTarget) {
      highBucket = index;
      break;
    }
  }

  const low = lowBucket / 255;
  const high = Math.max(low + 0.08, highBucket / 255);
  const normalized = new Float32Array(values.length);
  for (let index = 0; index < values.length; index += 1) {
    normalized[index] = clamp01((values[index] - low) / (high - low));
  }
  return normalized;
}

function addEdgeDetail(values: Float32Array, width: number, height: number) {
  const next = new Float32Array(values.length);
  const sample = (x: number, y: number) => values[Math.min(height - 1, Math.max(0, y)) * width + Math.min(width - 1, Math.max(0, x))];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const gx =
        -sample(x - 1, y - 1) + sample(x + 1, y - 1) +
        -2 * sample(x - 1, y) + 2 * sample(x + 1, y) +
        -sample(x - 1, y + 1) + sample(x + 1, y + 1);
      const gy =
        -sample(x - 1, y - 1) - 2 * sample(x, y - 1) - sample(x + 1, y - 1) +
        sample(x - 1, y + 1) + 2 * sample(x, y + 1) + sample(x + 1, y + 1);
      const edge = clamp01(Math.hypot(gx, gy) * 0.72);
      const base = values[y * width + x];
      next[y * width + x] = clamp01(base * 0.86 + edge * 0.42);
    }
  }

  return next;
}

function reliefCurve(value: number) {
  const lifted = Math.pow(clamp01(value), 0.72);
  return lifted * lifted * (3 - 2 * lifted);
}

function mappedHeight(darkness: number, mode: HeightmapOptions["mode"]) {
  if (mode === "engraved") return 1 - darkness;
  if (mode === "both") return Math.abs(darkness - 0.5) * 2;
  return darkness;
}

export async function createHeightmapStl(file: File, options: HeightmapOptions): Promise<HeightmapResult> {
  const image = await loadImage(file);
  const longest = 150;
  const gridWidth = image.width >= image.height ? longest : Math.max(16, Math.round(longest * image.width / image.height));
  const gridHeight = image.height >= image.width ? longest : Math.max(16, Math.round(longest * image.height / image.width));
  const canvas = document.createElement("canvas");
  canvas.width = gridWidth;
  canvas.height = gridHeight;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("IMAGE_CANVAS_UNAVAILABLE");
  context.drawImage(image, 0, 0, gridWidth, gridHeight);
  const pixels = context.getImageData(0, 0, gridWidth, gridHeight).data;
  let values = new Float32Array(gridWidth * gridHeight);
  for (let index = 0; index < values.length; index += 1) {
    const offset = index * 4;
    const luminance = (pixels[offset] * 0.2126 + pixels[offset + 1] * 0.7152 + pixels[offset + 2] * 0.0722) / 255;
    const alpha = pixels[offset + 3] / 255;
    values[index] = (1 - luminance) * alpha;
  }
  values = autoLevels(values);
  values = addEdgeDetail(values, gridWidth, gridHeight);
  if (options.smooth) {
    values = smoothValues(smoothValues(values, gridWidth, gridHeight), gridWidth, gridHeight);
  }
  for (let index = 0; index < values.length; index += 1) {
    values[index] = reliefCurve(mappedHeight(values[index], options.mode));
  }

  const physicalWidth = image.width >= image.height ? 60 : 60 * image.width / image.height;
  const physicalHeight = image.height >= image.width ? 60 : 60 * image.height / image.width;
  const baseThickness = 1.2;
  const depth = baseThickness + options.reliefHeight;
  const top = (x: number, y: number): Point => [
    physicalWidth * x / (gridWidth - 1) - physicalWidth / 2,
    physicalHeight / 2 - physicalHeight * y / (gridHeight - 1),
    baseThickness + values[y * gridWidth + x] * options.reliefHeight,
  ];
  const bottom = (x: number, y: number): Point => {
    const point = top(x, y);
    return [point[0], point[1], 0];
  };

  const triangles: Array<readonly [Point, Point, Point]> = [];
  for (let y = 0; y < gridHeight - 1; y += 1) {
    for (let x = 0; x < gridWidth - 1; x += 1) {
      triangles.push([top(x, y), top(x + 1, y + 1), top(x + 1, y)]);
      triangles.push([top(x, y), top(x, y + 1), top(x + 1, y + 1)]);
      triangles.push([bottom(x, y), bottom(x + 1, y), bottom(x + 1, y + 1)]);
      triangles.push([bottom(x, y), bottom(x + 1, y + 1), bottom(x, y + 1)]);
    }
  }
  const addEdge = (points: Array<readonly [number, number]>) => {
    for (let index = 0; index < points.length - 1; index += 1) {
      const [x1, y1] = points[index];
      const [x2, y2] = points[index + 1];
      triangles.push([top(x1, y1), bottom(x2, y2), bottom(x1, y1)]);
      triangles.push([top(x1, y1), top(x2, y2), bottom(x2, y2)]);
    }
  };
  addEdge(Array.from({ length: gridWidth }, (_, x) => [x, 0] as const));
  addEdge(Array.from({ length: gridHeight }, (_, y) => [gridWidth - 1, y] as const));
  addEdge(Array.from({ length: gridWidth }, (_, offset) => [gridWidth - 1 - offset, gridHeight - 1] as const));
  addEdge(Array.from({ length: gridHeight }, (_, offset) => [0, gridHeight - 1 - offset] as const));

  const buffer = new ArrayBuffer(84 + triangles.length * 50);
  const view = new DataView(buffer);
  view.setUint32(80, triangles.length, true);
  triangles.forEach(([a, b, c], index) => {
    const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const normal = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
    const length = Math.hypot(...normal) || 1;
    let offset = 84 + index * 50;
    [...normal.map((value) => value / length), ...a, ...b, ...c].forEach((value) => {
      view.setFloat32(offset, value, true);
      offset += 4;
    });
    view.setUint16(offset, 0, true);
  });

  return { blob: new Blob([buffer], { type: "model/stl" }), width: physicalWidth, height: physicalHeight, depth, triangleCount: triangles.length };
}
